// Alpine controller for the amenities CMS "grouping" widget.
//
// The widget lets an operator organise the property's canonical amenity pool
// into labelled tabs (e.g. "Kitchen", "Bathroom"). Each tab holds an ordered
// list of amenity ids; ids in NO tab fall through to a derived "More"/unassigned
// shelf rendered on the live page.
//
// PURE-DATA CORE: every interaction path (add/remove/reorder tabs, move a chip
// between tabs) is button- or menu-driven so the widget is fully functional
// WITHOUT drag. The per-chip "move ▾" menu (moveChip) is the RELIABLE fallback;
// SortableJS drag is the enhancement layered on top of the same pure-data methods.
//
// DRAG LAYER (the enhancement):
//   • OUTER Sortable on x-ref="shelfList" reorders the `tabs` array (handle =
//     [data-shelf-handle]). Because the shelf x-for is keyed by the STABLE
//     `tab._key`, Alpine MOVES the existing shelf DOM nodes on reorder rather than
//     re-creating them — so the INNER chip Sortables bound to those nodes survive a
//     tab reorder untouched.
//   • INNER Sortables — one per [data-chip-grid] (each shelf grid + the derived
//     unassigned grid), all in the shared group 'amenity-chips'. Same-grid drops
//     reorder that tab's amenity_ids (applySortableReorder); cross-grid drops go
//     through applySortableMove: revert Sortable's DOM move, then mutate BOTH
//     backing arrays (remove from source by [data-tab-key] of evt.from, insert into
//     target by [data-tab-key] of evt.to at newIndex). Chips are keyed by id so
//     Alpine re-renders the move correctly from the arrays.
//   • UNASSIGNED is DERIVED (unassigned()), not a stored array. Its grid carries
//     data-tab-key="" — applySortableMove treats a '' SOURCE as "no source splice"
//     (the id was derived) and a '' TARGET as "no insert" (equivalent to
//     moveChip(id, null): once removed from its source tab it re-derives into the
//     unassigned grid).
//   • Newly-added shelves (addTab) get their inner Sortable wired in a $nextTick
//     re-init pass, guarded idempotently via a data-sortable-bound attr so existing
//     grids aren't double-bound.
//
// Mirrors static/js/cms/surrounding-picker.ts for config-parse shape, the
// returned Alpine object shape, the Sortable init-in-$nextTick pattern, and the
// cms-image-changed emit pattern.

import Sortable from "sortablejs";
import {
  applySortableReorder,
  applySortableMove,
  createReorderable,
  type ChipGrid,
} from "../shared/sortable-reorder";
import { parseJsonOr } from "../shared/utils";

// One canonical amenity, projected from the property's amenity pool. `category`
// is the canonical grouping enum (used by the legacy-expand below) and `icon`
// rides along for editor-preview display.
interface AmenityItem {
  id: string;
  name: string;
  category?: string;
  icon?: string;
}

// A grouping tab. `_key` is a transient client-only handle used as the x-for key
// and to address tabs in the move-menu / reorder methods; it is stripped before
// the value is emitted.
interface AmenityTab {
  _key: string;
  label: string;
  amenity_ids: string[];
}

// The incoming tab shape may be EITHER the new {label, amenity_ids[]} OR the
// legacy {label, categories[]} (existing content). Both are normalised to
// AmenityTab on load.
interface RawAmenityTab {
  label?: string;
  amenity_ids?: string[];
  categories?: string[];
}

// The label+ids snapshot used for the rail summary (no transient _key).
interface ConfirmedTab {
  label: string;
  amenity_ids: string[];
}

interface CMSAmenityGroupingConfig {
  field: string;
  // Autosave routing key — threaded into the cms-image-changed detail so the
  // cmsEditor controller routes the write to the right section overlay.
  target: string;
  // Raw JSON strings from the widget ctx (parsed here so the templ never has to
  // build nested Alpine literals): pool = AmenityItem[], tabs = RawAmenityTab[].
  pool: string;
  tabs: string;
}

export function cmsAmenityGrouping(config: CMSAmenityGroupingConfig) {
  const pool = parseJsonOr(config.pool, [] as AmenityItem[]);

  const rawTabs = parseJsonOr(config.tabs, [] as RawAmenityTab[]);

  // Transient-key generator. Date is banned (and unnecessary) — a per-instance
  // incrementing counter is enough since keys only need to be unique within one
  // controller instance.
  let keySeq = 0;
  const nextKey = () => "t" + keySeq++;

  // The pool is the source of truth for which amenities EXIST. An amenity deleted
  // in the property hub disappears from the (cache-busted) pool but its id may
  // still linger in a saved tab's amenity_ids — drop those stale ids on load so
  // the modal never renders a ghost chip (byId undefined → fallback icon + raw
  // id). Filtering here also cleans the stale id from the content on next confirm.
  const poolIds = new Set(pool.map((a) => a.id));

  // Legacy-expand on load: a tab carrying `categories` but no `amenity_ids` is an
  // old-format tab. Expand it into the matching pool ids so that after load every
  // tab is uniformly {_key, label, amenity_ids}.
  const expand = (raw: RawAmenityTab): AmenityTab => {
    let ids: string[];
    if (raw.amenity_ids && raw.amenity_ids.length > 0) {
      ids = [...raw.amenity_ids];
    } else if (raw.categories && raw.categories.length > 0) {
      const cats = raw.categories;
      ids = pool.filter((a) => a.category != null && cats.includes(a.category)).map((a) => a.id);
    } else {
      ids = [...(raw.amenity_ids || [])];
    }
    // Drop ids no longer in the pool (amenity deleted in the hub).
    ids = ids.filter((id) => poolIds.has(id));
    return { _key: nextKey(), label: raw.label || "", amenity_ids: ids };
  };

  const tabs: AmenityTab[] = rawTabs.map(expand);

  // Rail summary snapshot — label+ids only, seeded from the loaded tabs.
  const confirmed: ConfirmedTab[] = tabs.map((t) => ({
    label: t.label,
    amenity_ids: [...t.amenity_ids],
  }));

  return {
    field: config.field,
    target: config.target,
    pool,
    tabs,
    search: "",
    // The id of the chip whose move-menu is currently open. '' = no open menu.
    // Drives moveMenuTabs() (the destinations for that chip) and is the target
    // of the moveChip(menuChipId, …) calls in the shared popover. Cleared on
    // close (explicit or browser light-dismiss via the popover @toggle handler).
    menuChipId: "" as string,
    // Rail-summary snapshot (only updated on confirm()).
    _confirmed: confirmed,
    // Pre-edit deep copy captured on openModal(), restored on discard().
    _preEdit: null as AmenityTab[] | null,
    // Live Sortable instances (1 outer + N inner) so destroy() can tear them down.
    _sortables: [] as Sortable[],

    // Alpine lifecycle. The native <dialog> content lives in the DOM even while
    // closed, so wiring Sortable on mount finds the grids. If none are found
    // (empty pool → modal not rendered), every helper no-ops.
    init() {
      (this as any).$nextTick(() => {
        this._initOuterSortable();
        this._initInnerSortables();
      });
    },

    destroy() {
      for (const s of this._sortables) s.destroy();
      this._sortables = [];
    },

    // The pool record for an id (name/category/icon read-only display).
    byId(id: string): AmenityItem | undefined {
      return this.pool.find((p) => p.id === id);
    },

    // Pool items whose id is in NO tab's amenity_ids — the derived "More"/
    // unassigned shelf the live page renders after the labelled tabs. Returns
    // IDS (not pool objects) so the templ's `x-for="id in unassigned()" :key="id"`
    // keys on a string and the shared chip markup (byId(id)/data-amenity-id="id")
    // works identically to the per-tab grids (`x-for="id in tab.amenity_ids"`).
    unassigned(): string[] {
      const assigned = new Set<string>();
      for (const t of this.tabs) {
        for (const id of t.amenity_ids) assigned.add(id);
      }
      return this.pool.filter((p) => !assigned.has(p.id)).map((p) => p.id);
    },

    // Count of unassigned amenities, derived from the CONFIRMED snapshot so the
    // rail summary matches the last-saved value (not in-flight modal edits).
    unassignedCount(): number {
      const assigned = new Set<string>();
      for (const t of this._confirmed) {
        for (const id of t.amenity_ids) assigned.add(id);
      }
      return this.pool.filter((p) => !assigned.has(p.id)).length;
    },

    // Rail summary rows from the CONFIRMED snapshot: one {label, count} per tab.
    // The unassigned row is rendered separately by the templ via unassignedCount().
    summary(): { label: string; count: number }[] {
      return this._confirmed.map((t) => ({ label: t.label, count: t.amenity_ids.length }));
    },

    // Snapshot the live tabs into a pre-edit deep copy so discard() can restore
    // cleanly. Uses a JSON round-trip (structuredClone equivalent; tabs are plain
    // JSON-safe data).
    openModal() {
      this._preEdit = JSON.parse(JSON.stringify(this.tabs)) as AmenityTab[];
    },

    addTab() {
      this.tabs.push({ _key: nextKey(), label: "", amenity_ids: [] });
      // Wire the new shelf's inner Sortable once Alpine has rendered its grid.
      // _initInnerSortables is idempotent (skips already-bound grids).
      (this as any).$nextTick(() => this._initInnerSortables());
    },

    // Drop the tab — its ids become unassigned automatically (no longer in any
    // tab's amenity_ids).
    removeTab(_key: string) {
      const i = this.tabs.findIndex((t) => t._key === _key);
      if (i === -1) return;
      this.tabs.splice(i, 1);
    },

    setLabel(_key: string, val: string) {
      const t = this.tabs.find((t) => t._key === _key);
      if (t) t.label = val;
    },

    moveTabUp(_key: string) {
      const i = this.tabs.findIndex((t) => t._key === _key);
      if (i <= 0) return;
      [this.tabs[i - 1], this.tabs[i]] = [this.tabs[i], this.tabs[i - 1]];
    },

    moveTabDown(_key: string) {
      const i = this.tabs.findIndex((t) => t._key === _key);
      if (i === -1 || i >= this.tabs.length - 1) return;
      [this.tabs[i], this.tabs[i + 1]] = [this.tabs[i + 1], this.tabs[i]];
    },

    // True when a chip matches the modal search box (case-insensitive substring
    // on the localized amenity name). Empty query → everything matches. Used by
    // the templ to DIM non-matching chips (not remove them — removing chips would
    // shift the SortableJS indices the drag layer relies on).
    matchesSearch(id: string): boolean {
      const q = this.search.trim().toLowerCase();
      if (!q) return true;
      const a = this.byId(id);
      return !!a && (a.name || "").toLowerCase().includes(q);
    },

    // Open the shared top-layer move-menu for one chip, anchored under its
    // trigger button. The Popover API renders the menu in the browser top layer
    // (never clipped by the modal's overflow scroll) and light-dismisses on
    // outside-click / Esc / re-open. Position is FIXED from the trigger's rect,
    // clamped so a left-edge chip's menu stays on-screen. Mirrors the "one shared
    // dialog" icon-picker pattern.
    openMoveMenu(id: string, ev: Event) {
      this.menuChipId = id;
      const pop = (this as any).$refs.moveMenu as HTMLElement | undefined;
      if (!pop) return;
      const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
      pop.style.position = "fixed";
      pop.style.top = r.bottom + 4 + "px";
      pop.style.left = Math.min(r.left, window.innerWidth - 220) + "px";
      pop.showPopover();
    },

    // Close the shared move-menu (explicit close after a pick). Guards hidePopover
    // — calling it on an already-hidden popover throws. The @toggle handler clears
    // menuChipId on browser light-dismiss; this clears it on the explicit path.
    closeMoveMenu() {
      this.menuChipId = "";
      const pop = (this as any).$refs.moveMenu as HTMLElement | undefined;
      if (pop && pop.matches(":popover-open")) pop.hidePopover();
    },

    // Destination tabs for the currently-open chip: the tabs it is NOT already in
    // (plus the templ-rendered "Unassigned" entry). Empty when no menu is open.
    moveMenuTabs(): AmenityTab[] {
      if (!this.menuChipId) return [];
      return this.tabs.filter((t) => !t.amenity_ids.includes(this.menuChipId));
    },

    // Move a chip to a tab (or to the unassigned shelf). Removes the id from
    // whatever tab currently holds it, then — if toKey names a tab — appends it
    // there; a null/'' toKey leaves it unassigned. Backs the "move ▾" menu.
    moveChip(id: string, toKey: string | null) {
      for (const t of this.tabs) {
        const i = t.amenity_ids.indexOf(id);
        if (i !== -1) t.amenity_ids.splice(i, 1);
      }
      if (toKey) {
        const dest = this.tabs.find((t) => t._key === toKey);
        if (dest && !dest.amenity_ids.includes(id)) dest.amenity_ids.push(id);
      }
    },

    // Commit the working copy: refresh the rail snapshot, then fire ONE whole-
    // value cms-image-changed event on $root (same channel surrounding-picker
    // uses) so the cmsEditor controller autosaves it into the section overlay.
    // The _key handle is stripped; empty tabs are KEPT.
    confirm() {
      this._confirmed = this.tabs.map((t) => ({ label: t.label, amenity_ids: [...t.amenity_ids] }));
      this._preEdit = null;
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: {
            field: this.field,
            target: this.target,
            value: this.tabs.map((t) => ({ label: t.label, amenity_ids: t.amenity_ids })),
          },
          bubbles: true,
        }),
      );
    },

    // Restore the pre-edit copy (no event fired).
    discard() {
      if (this._preEdit) {
        this.tabs = this._preEdit;
        this._preEdit = null;
      }
    },

    // OUTER Sortable: reorders the `tabs` array via the [data-shelf-handle] grip.
    // The keyed (tab._key) x-for means Alpine MOVES the shelf nodes on reorder, so
    // the inner chip Sortables bound to those nodes survive. No emit on reorder —
    // confirm() fires the single whole-value event.
    _initOuterSortable() {
      const el = (this as any).$refs.shelfList as HTMLElement | undefined;
      if (!el) return;
      this._sortables.push(
        createReorderable(el, {
          handle: "[data-shelf-handle]",
          draggable: "[data-shelf]",
          onEnd: (e: Sortable.SortableEvent) => {
            // Reorder rows by 'data-shelf' (the shelf marker), NOT 'data-sort-id':
            // chips also carry data-sort-id and querySelectorAll is recursive, so
            // 'data-sort-id' on the shelf list would match descendant chips and the
            // DOM-revert insertBefore would target a chip (not a shelf-list child).
            this.tabs = applySortableReorder(el, e, this.tabs, "data-shelf");
          },
        }),
      );
    },

    // INNER Sortables: one per [data-chip-grid] (each shelf + the unassigned grid),
    // all in the shared 'amenity-chips' group so chips drag between them. Idempotent:
    // grids already carrying data-sortable-bound are skipped, so it's safe to call
    // again after addTab() / structural changes.
    _initInnerSortables() {
      const root = (this as any).$root as HTMLElement | undefined;
      if (!root) return;
      const grids = root.querySelectorAll<HTMLElement>("[data-chip-grid]");
      grids.forEach((grid) => {
        if (grid.dataset.sortableBound === "1") return;
        grid.dataset.sortableBound = "1";
        this._sortables.push(
          createReorderable(grid, {
            group: "amenity-chips",
            draggable: "[data-chip]",
            onEnd: (e: Sortable.SortableEvent) => {
              if (e.from === e.to) {
                // Same-grid reorder: reorder that tab's amenity_ids. The unassigned
                // grid ('') has no backing array → nothing to reorder, skip.
                const key = e.from.getAttribute("data-tab-key") ?? "";
                const tab = this.tabs.find((t) => t._key === key);
                if (tab) {
                  tab.amenity_ids = applySortableReorder(
                    e.from as HTMLElement,
                    e,
                    tab.amenity_ids,
                    "data-sort-id",
                  );
                }
                return;
              }
              // Cross-grid move: revert the DOM, then mutate both arrays. Pass the
              // CURRENT grids (key → backing array; '' unassigned has none).
              const chipGrids: ChipGrid[] = this.tabs.map((t) => ({
                key: t._key,
                ids: t.amenity_ids,
              }));
              chipGrids.push({ key: "", ids: undefined });
              applySortableMove(e, chipGrids);
            },
          }),
        );
      });
    },
  };
}
